export interface CalendarEventData {
  title: string;
  description?: string;
  location?: string;
  date?: string;
}

/**
 * Format Date to UTC ISO string format without separators (YYYYMMDDTHHMMSSZ) for Google Calendar / iCal
 */
function formatCalendarDate(dateStr?: string, addHours = 2): { start: string; end: string } {
  let startDate = new Date();
  if (dateStr) {
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) {
      startDate = d;
    }
  }

  // Set default meeting time to 8:00 AM if hours are 00:00
  if (startDate.getHours() === 0 && startDate.getMinutes() === 0) {
    startDate.setHours(8, 0, 0, 0);
  }

  const endDate = new Date(startDate.getTime() + addHours * 60 * 60 * 1000);

  const toUtcString = (d: Date) => {
    return d.toISOString().replace(/-|:|\.\d+/g, '');
  };

  return {
    start: toUtcString(startDate),
    end: toUtcString(endDate)
  };
}

/**
 * Open Google Calendar pre-filled with meeting details for notification reminders
 */
export function openGoogleCalendar(event: CalendarEventData): void {
  const { start, end } = formatCalendarDate(event.date);
  const title = encodeURIComponent(event.title || 'RMB Chapter Meeting');
  const details = encodeURIComponent(event.description || 'RMB Jaipur Chapter Fellowship Meeting & Business Networking.');
  const location = encodeURIComponent(event.location || 'Jaipur, Rajasthan');

  const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${start}/${end}&details=${details}&location=${location}`;
  window.open(googleUrl, '_blank', 'noopener,noreferrer');
}

/**
 * Download standard .ics iCalendar file for Apple Calendar, Outlook & Android Native Calendars
 */
export function downloadIcsFile(event: CalendarEventData): void {
  const { start, end } = formatCalendarDate(event.date);
  const title = event.title || 'RMB Chapter Meeting';
  const details = (event.description || 'RMB Jaipur Chapter Fellowship Meeting').replace(/\n/g, '\\n');
  const location = event.location || 'Jaipur, Rajasthan';

  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//RMB Jaipur Fellowship//NONSGML v1.0//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `SUMMARY:${title}`,
    `DESCRIPTION:${details}`,
    `LOCATION:${location}`,
    `DTSTART:${start}`,
    `DTEND:${end}`,
    'STATUS:CONFIRMED',
    'BEGIN:VALARM',
    'TRIGGER:-PT30M',
    'ACTION:DISPLAY',
    'DESCRIPTION:Reminder: RMB Chapter Meeting in 30 minutes',
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\r\n');

  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${title.replace(/[^a-zA-Z0-9]/g, '_')}_Meeting.ics`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
